import type { TFunction } from 'i18next';
import type { TranslationKey } from '@finance/contracts';

/**
 * Branded i18next-form key returned only by the canonical namespace resolver.
 * Feature code cannot manufacture this accidentally from an arbitrary string.
 */
export type ResolvedTranslationKey = string & {
  readonly __resolvedTranslationKey: unique symbol;
};

/**
 * Translation interpolation/options accepted by the application boundary.
 * `defaultValue` is deliberately impossible: missing presentation copy must be
 * fixed in paired catalogs, never hidden by component-local English fallback.
 */
export type TranslationOptions = Record<string, unknown> & {
  readonly defaultValue?: never;
};

/**
 * Restricted translator used by reusable renderers/adapters. It accepts only a
 * compiler-known canonical key or a key already processed by resolveTKey.
 */
export type StaticTranslator = (
  key: TranslationKey | ResolvedTranslationKey,
  options?: TranslationOptions,
) => string;

export function createStaticTranslator(t: TFunction): StaticTranslator {
  return (key, options) => String(t(key as string, options));
}
